import { WA_PHYSICS } from '../core/constants';
import { getGameRNG } from '../core/rng';
import type { CollisionMask } from './collisionMask';
import type { EntityRegistry } from '../core/entityRegistry';
import type { WeaponSelectionManager } from '../core/weaponSelectionManager';
import type { GameState } from '../logic/gameState';
import type { Team } from '../logic/team';
import type { Worm } from '../entities/worm';
import type { Projectile } from '../entities/projectile';
import type { Fire } from '../entities/fire';
import type { Grave } from '../entities/grave';
import type { Crate } from '../entities/crate';
import type { OilBarrel } from '../entities/oilBarrel';
import type { Mine } from '../weapons/mine';
import type { HomingMissile } from '../weapons/homingMissile';
import type { Sheep } from '../weapons/sheep';
import type { MiniRaon } from '../weapons/raon';
import type { SuperSheep } from '../weapons/superSheep';
import type { Kamikaze } from '../weapons/kamikaze';
import type { NapalmDroplet } from '../weapons/napalmStrike';
import type { WeatherSystem } from './weather';
import { updateRopePhysics, releaseRope, isRopeActive } from '../weapons/ninjaRope';
import {
  activateBungee,
  updateBungeePhysics,
  isBungeeActive,
  releaseBungee,
} from '../weapons/bungee';
import { checkWormWormCollision, resolveWormCollision } from '../physics/wormCollision';
import {
  checkWormBarrelCollision,
  resolveWormBarrelCollision,
  checkProjectileBarrelCollision,
  handleProjectileBarrelHit,
} from '../physics/barrelCollision';

export interface PhysicsStepContext {
  state: GameState;
  terrain: CollisionMask;
  entityRegistry: EntityRegistry;
  weaponSelection: WeaponSelectionManager;
  getWorms(): Worm[];
  getWaterLevel(): number;
  getTerrainFriction(): number;
  applyJetpackTerrainCollision(worm: Worm): void;
  /** Active weather zones affecting projectiles (force/tornado/thor). */
  weather?: WeatherSystem;
}

export interface PhysicsStepCallbacks {
  playSound(soundId: string): void;
  stopSound(soundId: string): void;
  /** Presentation + drown-delay bookkeeping; called right before the worm dies. */
  onWormDrowned(worm: Worm, isActiveWorm: boolean): void;
  onBungeeAutoActivated(team: Team): void;
  onSheepLost(sheep: Sheep): void;
  onSuperSheepLost(superSheep: SuperSheep): void;
  /** A turn-ending action happened mid-physics (e.g. super sheep destroyed). */
  requestForceBroadcast(): void;
  isWaitingForCrates(): boolean;
  onCratesSettled(): void;
  destroyTerrain(x: number, y: number, radius: number): void;
  createExplosion(x: number, y: number, radius: number, maxDamage: number): void;
}

/**
 * Headless physics/entity update for one fixed timestep. Extracted verbatim
 * from Game.updatePhysics so it can run in the browser (local play) and on
 * the server (authoritative multiplayer) with identical behavior.
 */
export class PhysicsStep {
  constructor(
    private ctx: PhysicsStepContext,
    private cb: PhysicsStepCallbacks,
  ) {}

  update(deltaTime: number, isAuthority: boolean): void {
    const { state, terrain, entityRegistry } = this.ctx;
    const allWorms = this.ctx.getWorms();

    const platforms = this.getPlatformBounds();
    const terrainFriction = this.ctx.getTerrainFriction();

    for (const team of state.teams) {
      for (const worm of team.worms) {
        if (isRopeActive(worm)) {
          if (isAuthority) {
            updateRopePhysics(worm, terrain, deltaTime);
          }
        } else if (isBungeeActive(worm)) {
          if (isAuthority) {
            const bungeeFinished = updateBungeePhysics(worm, terrain, deltaTime);
            if (bungeeFinished && worm.id === state.getCurrentWorm()?.id) {
              state.onWeaponFired(true);
            }
          }
        } else if (worm.kamikazing) {
          worm.update(deltaTime, state.getWind(), terrain, terrainFriction);
          if (isAuthority) {
            this.handleKamikazeWorm(worm, allWorms);
          }
        } else if (worm.jetpackActive) {
          if (isAuthority) {
            this.ctx.applyJetpackTerrainCollision(worm);
          }
        } else {
          const wasGrounded = worm.isGrounded;
          worm.update(deltaTime, state.getWind(), terrain, terrainFriction);
          worm.applyTerrainPhysics(terrain, platforms);
          // Slope-relative aim (GunBound): keep the ground tilt under the tank
          // up to date while grounded so the aim span rotates with the terrain.
          worm.updateGroundTilt(terrain);

          if (
            isAuthority &&
            wasGrounded &&
            !worm.isGrounded &&
            !worm.parachuteOpen &&
            worm.id === state.getCurrentWorm()?.id &&
            this.ctx.weaponSelection.isWeaponSelected('bungee')
          ) {
            const wormTeam = state.teams.find((t) => t.id === worm.teamId);
            if (wormTeam && wormTeam.hasAmmo('bungee')) {
              activateBungee(worm);
              this.cb.playSound('bungee');
              wormTeam.useAmmo('bungee');
              this.cb.onBungeeAutoActivated(wormTeam);
              state.onWeaponFired(false);
            }
          }
        }

        const waterY = WA_PHYSICS.WORLD.HEIGHT - this.ctx.getWaterLevel();
        const isInWater = worm.position.y > waterY;

        if (isInWater && worm.state !== 'dead' && !worm.drowningSplashPlayed) {
          worm.drowningSplashPlayed = true;
          releaseRope(worm);
          releaseBungee(worm);

          const isActiveWorm = worm.id === state.getCurrentWorm()?.id;
          this.cb.onWormDrowned(worm, isActiveWorm);
          worm.die();
        }
      }
    }

    if (isAuthority) {
      for (const worm of allWorms) {
        const collision = checkWormWormCollision(worm, allWorms);
        if (collision) {
          resolveWormCollision(collision);
          this.cb.playSound('grenadeBounce');
        }
      }
    }

    for (const worm of allWorms) {
      if (worm.state === 'dead') continue;
      const barrel = checkWormBarrelCollision(worm, entityRegistry.getAll<OilBarrel>('barrel'));
      if (barrel) {
        resolveWormBarrelCollision(worm, barrel);
      }
    }

    const wind = state.getWind();
    for (const projectile of entityRegistry.getAll<Projectile>('projectile')) {
      const prevX = projectile.position.x;
      const prevY = projectile.position.y;

      projectile.update(deltaTime);
      projectile.applyWind(wind, deltaTime, state.windY);
      this.ctx.weather?.applyToProjectile(projectile, deltaTime);
      projectile.applyFriction();
      projectile.checkGroundBelow(terrain);

      if (projectile.type === 'adukaSS') {
        // Aduka SS: phases through terrain AND tanks, bursting each enemy tank it
        // passes through exactly once. Never collides or explodes.
        if (isAuthority) {
          this.applyAdukaSSDamage(projectile, allWorms);
        }
      } else if (projectile.pierceWorms) {
        // Grub SS: passes straight through tanks, electrocuting every enemy it
        // overlaps (damage-over-time). It still explodes for real when its
        // fuse expires.
        if (isAuthority) {
          this.applyGrubSSDamage(projectile, allWorms, deltaTime);
        }
      } else if (!(projectile.type === 'nak' && projectile.burrowing)) {
        const hitWorm = projectile.checkWormCollision(allWorms, prevX, prevY);
        if (hitWorm) {
          const result = projectile.handleWormHit(hitWorm);
          if (result === 'explode') {
            projectile.explode();
          } else if (result === 'bounce') {
            this.cb.playSound('grenadeBounce');
          }
        }
      }

      // Aduka tracers share an impact lock: once the first tracer has landed
      // and recorded the spot, the rest explode at that exact point as they
      // pass over it - so the whole volley lands together even though every
      // shot craters the ground (otherwise the later shots fall into the first
      // crater and sail off the map).
      if (
        projectile.active &&
        projectile.type === 'aduka' &&
        projectile.impactLock &&
        projectile.impactLock.x !== null &&
        projectile.impactLock.y !== null
      ) {
        const lx = projectile.impactLock.x;
        const ly = projectile.impactLock.y;
        if ((projectile.position.x - lx) * (prevX - lx) <= 0) {
          projectile.position.x = lx;
          projectile.position.y = ly;
          projectile.explode();
        }
      }

      // Nak T2 erupts the instant it climbs back out of the ground into air.
      if (projectile.active && projectile.type === 'nak' && projectile.burrowing) {
        if (!terrain.isSolid(projectile.position.x, projectile.position.y)) {
          projectile.explode();
        }
      }

      // A burrowing Nak shell ignores terrain collision (it tunnels through);
      // an Aduka SS phases through terrain entirely.
      if (
        projectile.active &&
        !projectile.phaseTerrain &&
        !(projectile.type === 'nak' && projectile.burrowing) &&
        projectile.checkTerrainCollision(terrain)
      ) {
        // First Aduka tracer to hit terrain locks the spot for its siblings.
        if (
          projectile.type === 'aduka' &&
          projectile.impactLock &&
          projectile.impactLock.x === null
        ) {
          projectile.impactLock.x = projectile.position.x;
          projectile.impactLock.y = projectile.position.y;
        }
        projectile.handleTerrainHit(terrain);
        if (
          projectile.type === 'grenade' ||
          projectile.type === 'cluster' ||
          projectile.type === 'grub'
        ) {
          this.cb.playSound('grenadeBounce');
        }
      }

      if (projectile.active) {
        const barrelHit = checkProjectileBarrelCollision(
          projectile,
          entityRegistry.getAll<OilBarrel>('barrel'),
        );
        if (barrelHit) {
          const result = handleProjectileBarrelHit(projectile, barrelHit);
          if (result === 'explode') {
            projectile.explode();
          } else if (result === 'bounce') {
            this.cb.playSound('grenadeBounce');
          }
        }
      }

      if (projectile.active) {
        for (const grave of entityRegistry.getAll<Grave>('grave')) {
          if (!grave.active) continue;
          const box = grave.getBounds();
          if (
            projectile.position.x >= box.x &&
            projectile.position.x <= box.x + box.width &&
            projectile.position.y >= box.y &&
            projectile.position.y <= box.y + box.height
          ) {
            if (projectile.type === 'bazooka' || projectile.type === 'clusterlet') {
              projectile.explode();
            } else {
              projectile.velocity.x *= -0.5;
              projectile.velocity.y *= -0.5;
              this.cb.playSound('grenadeBounce');
            }
            break;
          }
        }
      }

      if (projectile.checkWaterCollision(this.ctx.getWaterLevel(), WA_PHYSICS.WORLD.HEIGHT)) {
        projectile.destroy();
      }

      if (projectile.active) {
        const outOfBounds =
          projectile.position.x < -500 ||
          projectile.position.x > WA_PHYSICS.WORLD.WIDTH + 500 ||
          projectile.position.y < -1000;
        if (outOfBounds) {
          projectile.destroy();
        }
      }
    }

    entityRegistry.removeInactive('projectile');

    for (const mine of entityRegistry.getAll<Mine>('mine')) {
      mine.update(deltaTime);
      mine.applyTerrainPhysics(terrain);
      if (isAuthority && mine.checkTrigger(allWorms)) {
        mine.explode();
      }
    }
    entityRegistry.removeInactive('mine');

    for (const homing of entityRegistry.getAll<HomingMissile>('homingMissile')) {
      homing.update(deltaTime, state.wind);

      const hitWorm = homing.checkWormCollision(allWorms);
      if (hitWorm) {
        homing.explode();
      }

      if (homing.active && homing.checkTerrainCollision(terrain)) {
        homing.explode();
      }

      if (
        homing.active &&
        homing.checkWaterCollision(this.ctx.getWaterLevel(), WA_PHYSICS.WORLD.HEIGHT)
      ) {
        homing.destroy();
      }

      if (homing.active) {
        const outOfBounds =
          homing.position.x < -500 ||
          homing.position.x > WA_PHYSICS.WORLD.WIDTH + 500 ||
          homing.position.y < -1000;
        if (outOfBounds) {
          homing.destroy();
        }
      }
    }
    entityRegistry.removeInactive('homingMissile');

    for (const grave of entityRegistry.getAll<Grave>('grave')) {
      grave.update(deltaTime);
      grave.applyTerrainPhysics(terrain);
    }
    entityRegistry.removeInactive('grave');

    for (const crate of entityRegistry.getAll<Crate>('crate')) {
      crate.update(deltaTime);
      crate.applyTerrainPhysics(terrain);

      if (crate.position.y > WA_PHYSICS.WORLD.HEIGHT - this.ctx.getWaterLevel() + 20) {
        crate.destroy();
        continue;
      }

      if (isAuthority && crate.isGrounded) {
        const collectedBy = crate.checkWormCollision(allWorms);
        if (collectedBy) {
          crate.collect(collectedBy);
        }
      }
    }
    entityRegistry.removeInactive('crate');

    if (this.cb.isWaitingForCrates()) {
      const allCratesSettled = entityRegistry
        .getAll<Crate>('crate')
        .every((c) => c.isGrounded || !c.active);
      if (allCratesSettled || entityRegistry.count('crate') === 0) {
        this.cb.onCratesSettled();
      }
    }

    for (const barrel of entityRegistry.getAll<OilBarrel>('barrel')) {
      barrel.update(deltaTime);
      barrel.applyTerrainPhysics(terrain);

      if (barrel.position.y > WA_PHYSICS.WORLD.HEIGHT - this.ctx.getWaterLevel() + 20) {
        barrel.explode();
      }
    }
    entityRegistry.removeInactive('barrel');

    for (const sheep of entityRegistry.getAll<Sheep>('sheep')) {
      sheep.update(deltaTime, terrain);
      sheep.applyTerrainPhysics(terrain);

      if (sheep.checkWaterCollision(this.ctx.getWaterLevel(), WA_PHYSICS.WORLD.HEIGHT)) {
        sheep.destroy();
        this.cb.onSheepLost(sheep);
      }

      if (
        sheep.active &&
        (sheep.position.x < -500 || sheep.position.x > WA_PHYSICS.WORLD.WIDTH + 500)
      ) {
        sheep.destroy();
        this.cb.onSheepLost(sheep);
      }
    }
    entityRegistry.removeInactive('sheep');

    // Raon bots only walk while the turn is resolving / handing over, not
    // while a player is actively controlling their worm.
    const raonMayWalk = state.phase === 'damage_resolution' || state.phase === 'between_turns';
    for (const raon of entityRegistry.getAll<MiniRaon>('raon')) {
      raon.update(deltaTime, terrain, allWorms, state.turnSequence, raonMayWalk);
      raon.applyTerrainPhysics(terrain);

      if (
        raon.active &&
        raon.checkWaterCollision(this.ctx.getWaterLevel(), WA_PHYSICS.WORLD.HEIGHT)
      ) {
        raon.destroy();
      }
      if (
        raon.active &&
        (raon.position.x < -500 || raon.position.x > WA_PHYSICS.WORLD.WIDTH + 500)
      ) {
        raon.destroy();
      }
    }
    entityRegistry.removeInactive('raon');

    for (const ss of entityRegistry.getAll<SuperSheep>('superSheep')) {
      ss.update(deltaTime, terrain);
      if (!ss.isFlying) {
        ss.applyTerrainPhysics(terrain);
      }

      if (ss.checkWaterCollision(this.ctx.getWaterLevel(), WA_PHYSICS.WORLD.HEIGHT)) {
        ss.destroy();
        this.cb.stopSound('superSheepWhoosh');
        this.cb.onSuperSheepLost(ss);
        state.onWeaponFired(true);
        this.cb.requestForceBroadcast();
      }

      if (ss.active) {
        const outOfBounds =
          ss.position.x < -500 ||
          ss.position.x > WA_PHYSICS.WORLD.WIDTH + 500 ||
          ss.position.y < -1000;
        if (outOfBounds) {
          ss.destroy();
          this.cb.stopSound('superSheepWhoosh');
          this.cb.onSuperSheepLost(ss);
          state.onWeaponFired(true);
          this.cb.requestForceBroadcast();
        }
      }
    }
    entityRegistry.removeInactive('superSheep');

    for (const kamikaze of entityRegistry.getAll<Kamikaze>('kamikaze')) {
      kamikaze.update(deltaTime);

      kamikaze.checkTerrainAndDrill(terrain);

      const hitWormId = kamikaze.checkWormCollision(allWorms);
      if (hitWormId) {
        kamikaze.explode();
        continue;
      }

      if (kamikaze.position.y > WA_PHYSICS.WORLD.HEIGHT - this.ctx.getWaterLevel()) {
        kamikaze.explode();
      }
    }
    entityRegistry.removeInactive('kamikaze');

    for (const droplet of entityRegistry.getAll<NapalmDroplet>('napalmDroplet')) {
      droplet.update(deltaTime);

      if (droplet.checkTerrainCollision(terrain)) {
        droplet.land();
        continue;
      }

      if (droplet.checkWaterCollision(this.ctx.getWaterLevel(), WA_PHYSICS.WORLD.HEIGHT)) {
        droplet.destroy();
      }
    }
    entityRegistry.removeInactive('napalmDroplet');

    for (const fire of entityRegistry.getAll<Fire>('fire')) {
      fire.update(deltaTime);
      if (!fire.active) continue;

      for (const worm of allWorms) {
        if (worm.state === 'dead') continue;
        const box = worm.getBounds();
        if (fire.checkWormCollision(box.x, box.y, box.width, box.height)) {
          const damage = fire.accumulateDamage(deltaTime);
          if (damage > 0) {
            const pushX = getGameRNG().nextFloat(-20, 20);
            const pushY = getGameRNG().nextFloat(-35, -15);
            worm.addPendingDamage(damage);
            worm.applyExplosionPush(pushX, pushY);
          }
        }
      }
    }
    entityRegistry.removeInactive('fire');
  }

  /**
   * Grub SS damage-over-time: every tick the ball overlaps an enemy tank it
   * electrocutes them. Damage is accumulated to whole points (like fire) and
   * queued via pendingDamage so it resolves with the rest of the turn.
   */
  private applyGrubSSDamage(projectile: Projectile, worms: Worm[], deltaTime: number): void {
    const r = WA_PHYSICS.GRUB_SS.RADIUS;
    const px = projectile.position.x;
    const py = projectile.position.y;
    projectile.dotAccumulator += WA_PHYSICS.GRUB_SS.DOT_PER_SECOND * deltaTime;
    const whole = Math.floor(projectile.dotAccumulator);
    if (whole <= 0) return;
    projectile.dotAccumulator -= whole;

    for (const worm of worms) {
      if (worm.state === 'dead') continue;
      // Ignore the firer until the ball has bounced at least once (it spawns
      // right on top of the shooter).
      if (worm.id === projectile.ownerId && projectile.bounceCount === 0) continue;
      const box = worm.getBounds();
      const overlaps =
        px + r >= box.x &&
        px - r <= box.x + box.width &&
        py + r >= box.y &&
        py - r <= box.y + box.height;
      if (overlaps) {
        worm.addPendingDamage(whole);
      }
    }
  }

  /**
   * Aduka SS pierce burst: the shot phases through every enemy tank, dealing a
   * solid one-time burst to each (tracked in piercedWorms so a tank is never
   * double-counted as the shot overlaps it across several ticks).
   */
  private applyAdukaSSDamage(projectile: Projectile, worms: Worm[]): void {
    const r = WA_PHYSICS.ADUKA_SS.RADIUS;
    const px = projectile.position.x;
    const py = projectile.position.y;
    for (const worm of worms) {
      if (worm.state === 'dead') continue;
      if (worm.id === projectile.ownerId) continue; // never hits the firer
      if (projectile.piercedWorms.has(worm.id)) continue;
      const box = worm.getBounds();
      const overlaps =
        px + r >= box.x &&
        px - r <= box.x + box.width &&
        py + r >= box.y &&
        py - r <= box.y + box.height;
      if (overlaps) {
        projectile.piercedWorms.add(worm.id);
        worm.addPendingDamage(WA_PHYSICS.ADUKA_SS.DAMAGE_PER_TANK);
      }
    }
  }

  private handleKamikazeWorm(worm: Worm, allWorms: Worm[]): void {
    const centerX = worm.position.x + WA_PHYSICS.WORM.WIDTH / 2;
    const centerY = worm.position.y + WA_PHYSICS.WORM.HEIGHT / 2;
    const probeX = centerX + worm.kamikazeDirection.x * 15;
    const probeY = centerY + worm.kamikazeDirection.y * 15;

    if (this.ctx.terrain.isSolid(probeX, probeY)) {
      this.cb.destroyTerrain(centerX, centerY, 12);
    }

    for (const other of allWorms) {
      if (other.id === worm.id || other.state === 'dead') continue;
      const bounds = other.getBounds();
      const dx = centerX - (bounds.x + bounds.width / 2);
      const dy = centerY - (bounds.y + bounds.height / 2);
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 25) {
        this.kamikazeExplode(worm);
        return;
      }
    }

    const waterY = WA_PHYSICS.WORLD.HEIGHT - this.ctx.getWaterLevel();
    if (
      worm.position.y > waterY ||
      worm.position.x < 0 ||
      worm.position.x > WA_PHYSICS.WORLD.WIDTH ||
      worm.kamikazeDistance >= 300
    ) {
      this.kamikazeExplode(worm);
    }
  }

  private kamikazeExplode(worm: Worm): void {
    worm.kamikazing = false;
    const centerX = worm.position.x + WA_PHYSICS.WORM.WIDTH / 2;
    const centerY = worm.position.y + WA_PHYSICS.WORM.HEIGHT / 2;
    this.cb.createExplosion(centerX, centerY, 55, 55);
    worm.health = 0;
    worm.die();
  }

  private getPlatformBounds(): Array<{
    x: number;
    y: number;
    width: number;
    height: number;
  }> {
    const platforms: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
    }> = [];

    for (const barrel of this.ctx.entityRegistry.getAll<OilBarrel>('barrel')) {
      if (barrel.active) {
        platforms.push(barrel.getBounds());
      }
    }

    for (const crate of this.ctx.entityRegistry.getAll<Crate>('crate')) {
      if (crate.active) {
        platforms.push(crate.getBounds());
      }
    }

    return platforms;
  }
}
